import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  SportsBasketball as BasketballIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { format, isToday, isYesterday, isTomorrow } from 'date-fns';
import { GameFantasySummary, PlayerFantasyScore } from '../types';
import { nblService } from '../services/nblService';
import { usePlayer } from '../contexts/PlayerContext';
import { playerService } from '../services/playerService';
import { AdSenseBanner } from '../components/AdSenseBanner';
import { AD_SENSE_CONFIG } from '../config/adsense';

const ScoresPage: React.FC = () => {
  const { openPlayerDialog } = usePlayer();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [games, setGames] = useState<GameFantasySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<GameFantasySummary | null>(null);
  const [playerStats, setPlayerStats] = useState<PlayerFantasyScore[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [expandedGames, setExpandedGames] = useState<Set<number>>(new Set());
  const [selectedTeamTab, setSelectedTeamTab] = useState<number>(0);
  
  const handlePlayerNameClick = async (playerId: number) => {
    try {
      const player = await playerService.getPlayerById(playerId);
      if (player) {
        openPlayerDialog(player);
      }
    } catch (error) {
      console.error('Error fetching player:', error);
    }
  };

  // Load games for selected date
  const loadGames = useCallback(async (date: Date) => {
    setLoading(true);
    setError(null);
    
    try {
      const apiGames = await nblService.getGamesByDateWithFantasyScores(date);
      // Debug: Log games with timeRemaining/quarter to verify API response
      const liveGames = apiGames.filter(g => g.gameStatus === 'Live');
      if (liveGames.length > 0) {
        console.log('Live games with time data:', liveGames.map(g => ({
          id: g.gameId,
          status: g.gameStatus,
          quarter: g.quarter,
          timeRemaining: g.timeRemaining
        })));
      }
      setGames(apiGames || []);
    } catch (err) {
      console.error('Failed to load games:', err);
      setError('Failed to load games. Please try again later.');
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load player statistics for a game
  const loadPlayerStats = useCallback(async (gameId: number) => {
    setStatsLoading(true);
    
    try {
      const apiStats = await nblService.getPlayerGamesByGameId(gameId);
      setPlayerStats(apiStats || []);
    } catch (err) {
      console.error('Failed to load player statistics:', err);
      setPlayerStats([]);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      loadGames(date);
    }
  };

  // Handle game selection
  const handleGameSelect = async (game: GameFantasySummary) => {
    setSelectedGame(game);
    setSelectedTeamTab(0); // Reset to home team tab when opening a new game
    await loadPlayerStats(game.gameId);
  };

  // Toggle game expansion
  const toggleGameExpansion = (gameId: number) => {
    const newExpanded = new Set(expandedGames);
    if (newExpanded.has(gameId)) {
      newExpanded.delete(gameId);
    } else {
      newExpanded.add(gameId);
    }
    setExpandedGames(newExpanded);
  };



  // Format date display
  const formatDateDisplay = (date: Date) => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  // Get status color
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'live':
        return 'error';
      case 'final':
        return 'success';
      case 'scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get game status display
  const getGameStatusDisplay = (game: GameFantasySummary) => {
    if (game.gameStatus === 'Live' && game.gameStatus) {
      return game.gameStatus;
    }
    return game.gameStatus || 'Scheduled';
  };

  // Helper function to format time remaining
  const formatTimeRemaining = (timeRemaining?: string): string | null => {
    if (!timeRemaining) return null;
    
    // Handle ISO 8601 duration format (PT0S, PT5M30S, etc.)
    if (timeRemaining.startsWith('PT')) {
      const match = timeRemaining.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (match) {
        const hours = parseInt(match[1] || '0');
        const minutes = parseInt(match[2] || '0');
        const seconds = parseInt(match[3] || '0');
        
        if (hours > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else if (minutes > 0 || seconds > 0) {
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
      }
    }
    
    // If it's already in a readable format, return as is
    return timeRemaining;
  };

  // Load games on mount and date change
  useEffect(() => {
    loadGames(selectedDate);
  }, [selectedDate, loadGames]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: 3 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
          NBL Scores
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          View NBL game scores and player statistics
        </Typography>

        {/* Quick Stats Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                                 <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                   {games.filter(g => g.gameStatus === 'Final').length}
                 </Typography>
                <Typography variant="body2">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'error.light', color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                                 <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                   {games.filter(g => g.gameStatus === 'Live').length}
                 </Typography>
                <Typography variant="body2">Live Now</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                                 <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                   {games.filter(g => g.gameStatus === 'Scheduled').length}
                 </Typography>
                <Typography variant="body2">Upcoming</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {games.length}
                </Typography>
                <Typography variant="body2">Total Games</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Date Picker */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ScheduleIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatDateDisplay(selectedDate)}
              </Typography>
              <TextField
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    handleDateChange(date);
                  }
                }}
                variant="outlined"
                size="small"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Games List */}
        {!loading && !error && (
          <Box>
            {games.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No games scheduled for {formatDateDisplay(selectedDate)}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                                  {games.map((game) => (
                    <Grid size={{ xs: 12, md: 6 }} key={game.gameId}>
                    <Card>
                      <CardContent>
                        {/* Game Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {game.awayTeam} @ {game.homeTeam}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={getGameStatusDisplay(game)} 
                              color={getStatusColor(game.gameStatus) as any}
                              size="small"
                            />
                            {game.gameStatus === 'Live' && (game.timeRemaining || game.quarter) && (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                {game.quarter && `${game.quarter}`}
                                {game.quarter && game.timeRemaining && ` `}
                                {game.timeRemaining && (formatTimeRemaining(game.timeRemaining) || game.timeRemaining)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Game Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {game.venue || 'TBD'}
                          </Typography>
                        </Box>

                        {/* Score Display */}
                        {game.homeScore !== null && game.awayScore !== null && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            bgcolor: 'grey.100',
                            p: 2,
                            borderRadius: 1,
                            mb: 2
                          }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {game.awayScore}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {game.awayTeam}
                              </Typography>
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                              -
                            </Typography>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {game.homeScore}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {game.homeTeam}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Game Actions */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => toggleGameExpansion(game.gameId)}
                            endIcon={expandedGames.has(game.gameId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          >
                            {expandedGames.has(game.gameId) ? 'Hide Details' : 'Show Details'}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleGameSelect(game)}
                          >
                            View Box Score
                          </Button>
                        </Box>

                                                 {/* Expanded Game Details */}
                         {expandedGames.has(game.gameId) && (
                           <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                             {game.gameDate && (
                               <Typography variant="body2" color="text.secondary">
                                 <strong>Game Date:</strong> {format(new Date(game.gameDate), 'PPp')}
                               </Typography>
                             )}
                             {game.quarter && (
                               <Typography variant="body2" color="text.secondary">
                                 <strong>Quarter:</strong> {game.quarter}
                               </Typography>
                             )}
                             {game.timeRemaining && (
                               <Typography variant="body2" color="text.secondary">
                                 <strong>Time Remaining:</strong> {game.timeRemaining}
                               </Typography>
                             )}
                           </Box>
                         )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* Box Score Dialog */}
      <Dialog 
        open={!!selectedGame} 
        onClose={() => setSelectedGame(null)}
        maxWidth="lg"
        fullWidth
      >
        {selectedGame && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BasketballIcon color="primary" />
                <Typography variant="h6">
                  {selectedGame.awayTeam} @ {selectedGame.homeTeam} - Box Score
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  {/* Game Summary */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip 
                          label={getGameStatusDisplay(selectedGame)} 
                          color={getStatusColor(selectedGame.gameStatus) as any}
                          size="small"
                        />
                        {selectedGame.gameStatus === 'Live' && (selectedGame.timeRemaining || selectedGame.quarter) && (
                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            {selectedGame.quarter && `${selectedGame.quarter}`}
                            {selectedGame.quarter && selectedGame.timeRemaining && ` • `}
                            {selectedGame.timeRemaining && (formatTimeRemaining(selectedGame.timeRemaining) || selectedGame.timeRemaining)}
                          </Typography>
                        )}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {selectedGame.awayTeam}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {selectedGame.awayScore || 0}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {selectedGame.homeTeam}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {selectedGame.homeScore || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                      {selectedGame.gameDate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {format(new Date(selectedGame.gameDate), 'PPp')} • {selectedGame.venue || 'TBD'}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  {/* AdSense Banner - after initial scores section */}
                  {AD_SENSE_CONFIG.BOX_SCORE_SLOT !== 'YOUR_AD_SLOT_ID_HERE' && (
                    <AdSenseBanner 
                      adSlot={AD_SENSE_CONFIG.BOX_SCORE_SLOT} 
                      spacing={2}
                      reloadKey={selectedGame.gameId}
                      sx={{ my: 2 }}
                    />
                  )}

                  {/* Top Performers Summary */}
                  {playerStats.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                          Top Performers
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center', p: 1 }}>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {Math.max(...playerStats.map(p => p.points))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Points Leader
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center', p: 1 }}>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {Math.max(...playerStats.map(p => p.rebounds))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Rebounds Leader
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center', p: 1 }}>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {Math.max(...playerStats.map(p => p.assists))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Assists Leader
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center', p: 1 }}>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {Math.max(...playerStats.map(p => p.steals))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Steals Leader
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center', p: 1 }}>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {Math.max(...playerStats.map(p => p.fantasyPoints || 0)).toFixed(1)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Fantasy Points Leader
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}

                  {/* Player Statistics Table */}
                  {playerStats.length > 0 ? (
                    <Box>
                      {/* Team Tabs */}
                      <Tabs 
                        value={selectedTeamTab} 
                        onChange={(_, newValue) => setSelectedTeamTab(newValue)}
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                      >
                        <Tab label={selectedGame.homeTeam} />
                        <Tab label={selectedGame.awayTeam} />
                      </Tabs>

                      {/* Filtered and Sorted Player Stats */}
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Player</TableCell>
                              <TableCell>Team</TableCell>
                              <TableCell>Pos</TableCell>
                              <TableCell>Min</TableCell>
                              <TableCell>FG</TableCell>
                              <TableCell>3P</TableCell>
                              <TableCell>FT</TableCell>
                              <TableCell>PTS</TableCell>
                              <TableCell>REB</TableCell>
                              <TableCell>OREB</TableCell>
                              <TableCell>DREB</TableCell>
                              <TableCell>AST</TableCell>
                              <TableCell>STL</TableCell>
                              <TableCell>BLK</TableCell>
                              <TableCell>TO</TableCell>
                              <TableCell>PF</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>FP</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {playerStats
                              .filter((player) => {
                                if (selectedTeamTab === 0) {
                                  return player.team === selectedGame.homeTeam;
                                } else {
                                  return player.team === selectedGame.awayTeam;
                                }
                              })
                              .sort((a, b) => b.minutesPlayed - a.minutesPlayed)
                              .map((player) => (
                                <TableRow key={player.id}>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 'bold', 
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                                    }}
                                    onClick={() => handlePlayerNameClick(player.playerId)}
                                  >
                                    {player.playerName}
                                  </TableCell>
                                  <TableCell>{player.team}</TableCell>
                                  <TableCell>{player.position}</TableCell>
                                  <TableCell>{Math.floor(player.minutesPlayed / 60)}:{(player.minutesPlayed % 60).toString().padStart(2, '0')}</TableCell>
                                  <TableCell>{player.fieldGoalsMade}/{player.fieldGoalsAttempted}</TableCell>
                                  <TableCell>{player.threePointersMade}/{player.threePointersAttempted}</TableCell>
                                  <TableCell>{player.freeThrowsMade}/{player.freeThrowsAttempted}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>{player.points}</TableCell>
                                  <TableCell>{player.rebounds}</TableCell>
                                  <TableCell>{player.offensiveRebounds}</TableCell>
                                  <TableCell>{player.defensiveRebounds}</TableCell>
                                  <TableCell>{player.assists}</TableCell>
                                  <TableCell>{player.steals}</TableCell>
                                  <TableCell>{player.blocks}</TableCell>
                                  <TableCell>{player.turnovers}</TableCell>
                                  <TableCell>{player.fouls}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {player.fantasyPoints?.toFixed(1) || '0.0'}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No player statistics available for this game.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedGame(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ScoresPage;
